import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import Logger from "../../services/Logger";
import PromotionEmailService from "./PromotionEmailService";

const APPLE_PRIVATE_RELAY_SUFFIX = "@privaterelay.appleid.com";
const STORAGE_PREFIX = "admin.advertisement.promotion.recipients";
const EXCLUDE_STAFF_KEY = `${STORAGE_PREFIX}.excludeStaff`;
const EXCLUDE_APPLE_KEY = `${STORAGE_PREFIX}.excludeAppleRelay`;
const EXCLUDE_ONE_TIME_KEY = `${STORAGE_PREFIX}.excludeOneTimePlay`;
const MAILING_FILTER_KEY = `${STORAGE_PREFIX}.mailingFilter`;
const MAILING_FILTER_ALL = "all";
const MAILING_FILTER_OK = "ok";
const MAILING_FILTER_UNSUBSCRIBED = "unsubscribed";

function persistItem(key, value) {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        /* private mode */
    }
}

function loadStoredBool(key, fallback) {
    try {
        const stored = window.localStorage.getItem(key);
        if (stored === "true") return true;
        if (stored === "false") return false;
    } catch {
        /* private mode */
    }
    return fallback;
}

function loadStoredMailingFilter() {
    try {
        const stored = window.localStorage.getItem(MAILING_FILTER_KEY);
        if (
            stored === MAILING_FILTER_ALL ||
            stored === MAILING_FILTER_OK ||
            stored === MAILING_FILTER_UNSUBSCRIBED
        ) {
            return stored;
        }
    } catch {
        /* private mode */
    }
    return MAILING_FILTER_OK;
}

function formatPlayTime(value) {
    if (value == null || value === "") return "—";
    const d = dayjs(value);
    return d.isValid() ? d.format("YYYY-MM-DD HH:mm") : "—";
}

function formatDurationMs(ms) {
    if (ms == null || !Number.isFinite(ms) || ms < 0) return "—";
    const totalDays = Math.floor(ms / 86_400_000);
    if (totalDays < 1) return "<1d";
    if (totalDays < 30) return `${totalDays}d`;
    const months = Math.floor(totalDays / 30);
    if (months < 12) return `${months}mo`;
    const years = Math.floor(months / 12);
    const remMonths = months % 12;
    return remMonths > 0 ? `${years}y ${remMonths}mo` : `${years}y`;
}

function isApplePrivateRelayEmail(email) {
    if (!email || typeof email !== "string") return false;
    return email.trim().toLowerCase().endsWith(APPLE_PRIVATE_RELAY_SUFFIX);
}

function recipientRowId(row) {
    return row?.cloudUserId || row?.id || row?.email;
}

function selectedIdSet(selectedRecipients) {
    const ids = new Set();
    (selectedRecipients || []).forEach((row) => {
        const id = recipientRowId(row);
        if (id) ids.add(id);
        if (row?.email) ids.add(row.email);
        if (row?.cloudUserId) ids.add(row.cloudUserId);
    });
    return ids;
}

function toGridRow(u, app) {
    const firstPlayedAt = u.firstPlayedAt ?? null;
    const lastPlayedAt = u.lastPlayedAt ?? null;
    const firstMs = firstPlayedAt != null ? Number(firstPlayedAt) : NaN;
    const lastMs = lastPlayedAt != null ? Number(lastPlayedAt) : NaN;
    const playDurationMs =
        Number.isFinite(firstMs) && Number.isFinite(lastMs) && lastMs >= firstMs
            ? lastMs - firstMs
            : null;
    return {
        id: recipientRowId(u),
        cloudUserId: u.cloudUserId,
        name: u.name || null,
        email: u.email,
        app: u.app || app,
        playCount: u.playCount ?? 0,
        firstPlayedAt,
        lastPlayedAt,
        playDurationMs,
        lastPromoEmailSentAt: u.lastPromoEmailSentAt ?? null,
        pushNotificationsEnabled: Boolean(u.pushNotificationsEnabled ?? u.notificationsEnabled),
        emailOptedOut: Boolean(u.emailOptedOut),
        staff: Boolean(u.staff),
    };
}

function asSavedRecipient(row) {
    return {
        cloudUserId: row.cloudUserId,
        email: row.email,
        name: row.name || null,
        app: row.app,
        pushNotificationsEnabled: row.pushNotificationsEnabled,
        emailOptedOut: row.emailOptedOut,
    };
}

function uniqueRecipientsByEmail(list) {
    const seen = new Set();
    const out = [];
    for (const row of list || []) {
        const email = typeof row?.email === "string" ? row.email.trim().toLowerCase() : "";
        if (!email || seen.has(email)) continue;
        seen.add(email);
        out.push(row);
    }
    return out;
}

/**
 * Pick real app users for READY_TO_SENT recipients. Shows play count + notification preference.
 */
export default function PromotionRecipientsDialog({
    open,
    onClose,
    app,
    selectedRecipients,
    onSave,
}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [rows, setRows] = useState([]);
    const [selection, setSelection] = useState([]);
    const [keyword, setKeyword] = useState("");
    /** User Details → Staff (company accounts). Not Apple Hide My Email. */
    const [excludeStaff, setExcludeStaff] = useState(() => loadStoredBool(EXCLUDE_STAFF_KEY, true));
    /** Hide @privaterelay.appleid.com (default on). */
    const [excludeAppleStaff, setExcludeAppleStaff] = useState(() =>
        loadStoredBool(EXCLUDE_APPLE_KEY, true)
    );
    /** Hide users with exactly one play (default on). */
    const [excludeOneTimePlay, setExcludeOneTimePlay] = useState(() =>
        loadStoredBool(EXCLUDE_ONE_TIME_KEY, true)
    );
    /** all | ok | unsubscribed */
    const [mailingFilter, setMailingFilter] = useState(loadStoredMailingFilter);

    const persistExcludeStaff = useCallback((checked) => {
        setExcludeStaff(checked);
        persistItem(EXCLUDE_STAFF_KEY, checked ? "true" : "false");
    }, []);

    const persistExcludeApple = useCallback((checked) => {
        setExcludeAppleStaff(checked);
        persistItem(EXCLUDE_APPLE_KEY, checked ? "true" : "false");
    }, []);

    const persistExcludeOneTime = useCallback((checked) => {
        setExcludeOneTimePlay(checked);
        persistItem(EXCLUDE_ONE_TIME_KEY, checked ? "true" : "false");
    }, []);

    const persistMailingFilter = useCallback((value) => {
        setMailingFilter(value);
        persistItem(MAILING_FILTER_KEY, value);
    }, []);

    useEffect(() => {
        if (!open) return;
        setKeyword("");
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError("");
            try {
                const data = await PromotionEmailService.recipientCandidates(app);
                if (cancelled) return;
                const users = Array.isArray(data?.users) ? data.users : [];
                const mapped = users.map((u) => toGridRow(u, app)).filter((row) => row.id);
                const knownIds = new Set();
                mapped.forEach((row) => {
                    knownIds.add(row.id);
                    if (row.cloudUserId) knownIds.add(row.cloudUserId);
                    if (row.email) knownIds.add(row.email);
                });
                const extras = (selectedRecipients || [])
                    .filter((row) => {
                        const id = recipientRowId(row);
                        if (!id) return false;
                        if (knownIds.has(id)) return false;
                        if (row.email && knownIds.has(row.email)) return false;
                        return true;
                    })
                    .map((row) => toGridRow(row, app));
                const nextRows = [...mapped, ...extras];
                setRows(nextRows);
                const assigned = selectedIdSet(selectedRecipients);
                setSelection(
                    nextRows
                        .filter(
                            (row) =>
                                !row.emailOptedOut &&
                                (assigned.has(row.id) ||
                                    assigned.has(row.cloudUserId) ||
                                    (row.email && assigned.has(row.email)))
                        )
                        .map((row) => row.id)
                );
            } catch (err) {
                Logger.error("recipient candidates:", err);
                if (!cancelled) {
                    setError(typeof err === "string" ? err : err?.message || "Failed to load users");
                    setRows([]);
                    setSelection([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [open, app, selectedRecipients]);

    const selectionSet = useMemo(() => new Set(selection), [selection]);

    const visibleRows = useMemo(() => {
        const needle = keyword.trim().toLowerCase();
        return rows.filter((r) => {
            if (needle) {
                const haystack = `${r.name || ""} ${r.email || ""}`.toLowerCase();
                if (!haystack.includes(needle)) return false;
            }
            if (selectionSet.has(r.id) && !r.emailOptedOut) return true;
            if (excludeStaff && r.staff) return false;
            if (excludeAppleStaff && isApplePrivateRelayEmail(r.email)) return false;
            if (excludeOneTimePlay && Number(r.playCount) === 1) return false;
            if (mailingFilter === MAILING_FILTER_OK && r.emailOptedOut) return false;
            if (mailingFilter === MAILING_FILTER_UNSUBSCRIBED && !r.emailOptedOut) return false;
            return true;
        });
    }, [
        rows,
        excludeStaff,
        excludeAppleStaff,
        excludeOneTimePlay,
        mailingFilter,
        keyword,
        selectionSet,
    ]);

    const visibleIdSet = useMemo(() => new Set(visibleRows.map((r) => r.id)), [visibleRows]);

    const visibleSelectionModel = useMemo(
        () => selection.filter((id) => visibleIdSet.has(id)),
        [selection, visibleIdSet]
    );

    const hiddenStaffCount = useMemo(
        () =>
            excludeStaff
                ? rows.filter((r) => r.staff && !selectionSet.has(r.id)).length
                : 0,
        [rows, excludeStaff, selectionSet]
    );

    const hiddenAppleCount = useMemo(
        () =>
            excludeAppleStaff
                ? rows.filter((r) => isApplePrivateRelayEmail(r.email) && !selectionSet.has(r.id))
                      .length
                : 0,
        [rows, excludeAppleStaff, selectionSet]
    );

    const hiddenOneTimePlayCount = useMemo(
        () =>
            excludeOneTimePlay
                ? rows.filter((r) => Number(r.playCount) === 1 && !selectionSet.has(r.id)).length
                : 0,
        [rows, excludeOneTimePlay, selectionSet]
    );

    const unsubscribedCount = useMemo(
        () => rows.filter((r) => r.emailOptedOut).length,
        [rows]
    );

    const handleSelectionChange = useCallback(
        (newModel) => {
            const nextVisible = Array.isArray(newModel)
                ? newModel
                : newModel?.ids
                  ? Array.from(newModel.ids)
                  : [];
            const optedOutIds = new Set(
                rows.filter((r) => r.emailOptedOut).map((r) => r.id)
            );
            const allowedVisible = nextVisible.filter((id) => !optedOutIds.has(id));
            setSelection((prev) => [
                ...prev.filter((id) => !visibleIdSet.has(id) && !optedOutIds.has(id)),
                ...allowedVisible,
            ]);
        },
        [visibleIdSet, rows]
    );

    const columns = useMemo(
        () => [
            {
                field: "recipient",
                headerName: "Name / Email",
                flex: 1.4,
                minWidth: 200,
                sortable: false,
                valueGetter: (_value, row) => row?.name || row?.email || "",
                renderCell: (params) => {
                    const name = params.row?.name;
                    const email = params.row?.email || "—";
                    return (
                        <Box sx={{ py: 0.5, lineHeight: 1.25 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                                <Typography variant="body2" component="div" noWrap title={name || undefined}>
                                    {name || "—"}
                                </Typography>
                                {params.row?.staff ? (
                                    <Chip size="small" label="Staff" variant="outlined" />
                                ) : null}
                            </Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                component="div"
                                noWrap
                                title={email}
                            >
                                {email}
                            </Typography>
                        </Box>
                    );
                },
            },
            {
                field: "app",
                headerName: "App",
                width: 130,
                valueFormatter: (value) => value || "—",
            },
            {
                field: "playCount",
                headerName: "Plays",
                width: 80,
                valueFormatter: (value) =>
                    Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "—",
            },
            {
                field: "playRange",
                headerName: "First / Last play",
                width: 160,
                sortable: false,
                valueGetter: (_value, row) => row?.lastPlayedAt ?? row?.firstPlayedAt ?? null,
                renderCell: (params) => (
                    <Box sx={{ py: 0.5, lineHeight: 1.25 }}>
                        <Typography variant="body2" component="div" noWrap>
                            {formatPlayTime(params.row?.firstPlayedAt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div" noWrap>
                            {formatPlayTime(params.row?.lastPlayedAt)}
                        </Typography>
                    </Box>
                ),
            },
            {
                field: "playDurationMs",
                headerName: "Duration",
                width: 100,
                valueFormatter: (value) => formatDurationMs(value),
            },
            {
                field: "lastPromoEmailSentAt",
                headerName: "Last sent",
                width: 150,
                valueFormatter: (value) => formatPlayTime(value),
            },
            {
                field: "emailOptedOut",
                headerName: "Mailing",
                width: 130,
                renderCell: (params) =>
                    params.value ? (
                        <Chip size="small" color="warning" label="Unsubscribed" variant="outlined" />
                    ) : (
                        <Chip size="small" color="success" label="OK" />
                    ),
            },
            {
                field: "pushNotificationsEnabled",
                headerName: "Push",
                width: 90,
                renderCell: (params) =>
                    params.value ? (
                        <Chip size="small" color="success" label="On" />
                    ) : (
                        <Chip size="small" label="Off" variant="outlined" />
                    ),
            },
        ],
        []
    );

    const handleSave = () => {
        const byId = new Map();
        rows.forEach((row) => {
            if (row.id) byId.set(row.id, row);
            if (row.cloudUserId) byId.set(row.cloudUserId, row);
            if (row.email) byId.set(row.email, row);
        });
        (selectedRecipients || []).forEach((row) => {
            const id = recipientRowId(row);
            if (id && !byId.has(id)) byId.set(id, toGridRow(row, app));
        });
        const next = uniqueRecipientsByEmail(
            selection
                .map((id) => byId.get(id))
                .filter((row) => row && !row.emailOptedOut)
                .map(asSavedRecipient)
        );
        onSave(next);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
            <DialogTitle>Assign READY_TO_SENT recipients</DialogTitle>
            <DialogContent>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 1,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Signed-in users with play records for this app. Last sent = last promo email
                        for this app. Unsubscribed addresses cannot be selected or saved. Mailing =
                        list opt-out; Push = mobile preference.
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                        <TextField
                            size="small"
                            label="Search name or email"
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            sx={{ minWidth: 220 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel id="promo-mailing-filter-label">Mailing</InputLabel>
                            <Select
                                labelId="promo-mailing-filter-label"
                                label="Mailing"
                                value={mailingFilter}
                                onChange={(e) => persistMailingFilter(e.target.value)}
                            >
                                <MenuItem value={MAILING_FILTER_ALL}>All</MenuItem>
                                <MenuItem value={MAILING_FILTER_OK}>OK only</MenuItem>
                                <MenuItem value={MAILING_FILTER_UNSUBSCRIBED}>
                                    Unsubscribed
                                    {unsubscribedCount > 0 ? ` (${unsubscribedCount})` : ""}
                                </MenuItem>
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={excludeStaff}
                                    onChange={(e) => persistExcludeStaff(e.target.checked)}
                                />
                            }
                            label="Exclude staff"
                            sx={{ mr: 0, whiteSpace: "nowrap" }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={excludeAppleStaff}
                                    onChange={(e) => persistExcludeApple(e.target.checked)}
                                />
                            }
                            label="Exclude Apple relay"
                            sx={{ mr: 0, whiteSpace: "nowrap" }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={excludeOneTimePlay}
                                    onChange={(e) => persistExcludeOneTime(e.target.checked)}
                                />
                            }
                            label="Exclude 1 time play"
                            sx={{ mr: 0, whiteSpace: "nowrap" }}
                        />
                    </Box>
                </Box>
                {excludeStaff ||
                excludeAppleStaff ||
                excludeOneTimePlay ||
                mailingFilter !== MAILING_FILTER_ALL ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        {[
                            excludeStaff
                                ? "Hiding User Details → Staff accounts (not Apple Hide My Email)"
                                : null,
                            excludeAppleStaff
                                ? `Hiding emails ending with ${APPLE_PRIVATE_RELAY_SUFFIX}`
                                : null,
                            excludeOneTimePlay ? "Hiding users with exactly 1 play" : null,
                            mailingFilter === MAILING_FILTER_OK
                                ? "Showing mailing OK only"
                                : mailingFilter === MAILING_FILTER_UNSUBSCRIBED
                                  ? "Showing unsubscribed only"
                                  : null,
                        ]
                            .filter(Boolean)
                            .join(" · ")}
                    </Typography>
                ) : null}
                {error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : null}
                <Box sx={{ height: "min(70vh, 720px)", width: "100%" }}>
                    <DataGrid
                        rows={visibleRows}
                        columns={columns}
                        checkboxSelection
                        keepNonExistentRowsSelected
                        isRowSelectable={(params) => !params.row?.emailOptedOut}
                        rowSelectionModel={visibleSelectionModel}
                        onRowSelectionModelChange={handleSelectionChange}
                        loading={loading}
                        disableRowSelectionOnClick
                        getRowHeight={() => 56}
                        pageSizeOptions={[25, 50, 100]}
                        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                        localeText={{
                            noRowsLabel: loading ? "Loading…" : "No players for this app.",
                        }}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {selection.length} selected
                    {unsubscribedCount > 0 ? ` · ${unsubscribedCount} unsubscribed` : ""}
                    {hiddenStaffCount > 0 ? ` · ${hiddenStaffCount} staff hidden` : ""}
                    {hiddenAppleCount > 0
                        ? ` · ${hiddenAppleCount} Apple private relay hidden`
                        : ""}
                    {hiddenOneTimePlayCount > 0
                        ? ` · ${hiddenOneTimePlayCount} one-time play hidden`
                        : ""}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={{ textTransform: "none" }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                    sx={{ textTransform: "none" }}
                >
                    {loading ? <CircularProgress size={18} /> : "Save recipients"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
